import type { ScreenerConditionInput, ScreenerGroupInput } from '../types/screenerEditor';

function toNumber(value: unknown): number | null {
    if (typeof value !== 'number') return null;
    return Number.isFinite(value) ? value : null;
}

function clamp01Number(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.min(1, Math.max(0, value));
}

export function formatPct01Number(value: number, decimals: number = 1): string {
    const pct = clamp01Number(value) * 100;
    return `${pct.toFixed(decimals)}%`;
}

export function formatSignedPct(value: number, decimals: number = 2): string {
    if (!Number.isFinite(value)) return '—';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(decimals)}%`;
}

export function formatUsd(value: number, decimals: number = 2): string {
    if (!Number.isFinite(value)) return '—';

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });

    if (value < 0) return `-${formatter.format(Math.abs(value))}`;
    if (value > 0) return `+${formatter.format(value)}`;
    return formatter.format(0);
}

export function profitColorClass(value: number): string {
    if (!Number.isFinite(value) || value === 0) return 'text-zinc-300';
    return value > 0 ? 'text-green-400' : 'text-red-400';
}

function formatConditionValue(c: ScreenerConditionInput): string {
    if ('value' in c) return String(c.value);
    if ('values' in c) return c.values.map((v) => String(v)).join(', ');
    return '';
}

function formatConditionLine(c: ScreenerConditionInput): string {
    const pieces: string[] = [`${c.featureKey} ${c.operator}`];

    const valueStr = formatConditionValue(c);
    if (valueStr.length) pieces.push(valueStr);

    const extras: string[] = [];
    if (c.duration) extras.push(`dur=${c.duration}`);
    if (toNumber(c.lookbackBars) !== null) extras.push(`lb=${c.lookbackBars}`);
    if (c.missingPolicy && c.missingPolicy !== 'failClosed') extras.push(`missing=${c.missingPolicy}`);

    if (extras.length) pieces.push(`[${extras.join(' ')}]`);
    return pieces.join(' ');
}

export function screenerGroupInputToLines(root: ScreenerGroupInput): string[] {
    const lines: string[] = [];

    const walk = (node: ScreenerGroupInput | ScreenerConditionInput, depth: number) => {
        const indent = '  '.repeat(depth);

        if (node.type === 'group') {
            lines.push(`${indent}(${node.operator.toUpperCase()})`);
            for (const child of node.children) walk(child, depth + 1);
            return;
        }

        lines.push(`${indent}${formatConditionLine(node)}`);
    };

    walk(root, 0);
    return lines;
}

export function diffScreenerGroupInputs(a: ScreenerGroupInput, b: ScreenerGroupInput): { added: string[]; removed: string[] } {
    const linesA = screenerGroupInputToLines(a);
    const linesB = screenerGroupInputToLines(b);

    const setA = new Set(linesA);
    const setB = new Set(linesB);

    const added = linesB.filter((l) => !setA.has(l));
    const removed = linesA.filter((l) => !setB.has(l));

    return { added, removed };
}

export function tryFormatExperimentDate(iso: string): string {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    return dt.toLocaleString();
}
